import React, { useState, useEffect, useRef } from 'react';
import { CardElement, useStripe, useElements, Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import pb from '../lib/pocketbase';
import { FaCreditCard, FaLock, FaCcVisa, FaCcMastercard, FaCcAmex, FaCcDiscover, FaBuilding, FaUsers, FaChartLine } from 'react-icons/fa';
import { getAuthToken } from '../components/utils/pocketbaseApi';
import { useCompany } from '../context/CompanyContext';

// Stripe publishable keys from env (set via .env locally or Portainer at runtime)
const STRIPE_TEST_KEY = process.env.REACT_APP_STRIPE_PK;
const STRIPE_LIVE_KEY = process.env.REACT_APP_STRIPE_PK_LIVE;
/**
 * @typedef {Object} Card
 * @property {string} id - The card's unique identifier
 * @property {string} brand - The card brand (visa, mastercard, etc.)
 * @property {string} last4 - The last 4 digits of the card number
 * @property {number} expMonth - The expiration month
 * @property {number} expYear - The expiration year
 */

/**
 * @param {Object} props
 * @param {Card} props.card - The card object to display
 */
const RotatableCard = ({ card }) => {
  const cardRef = useRef(null);
  const [rotation, setRotation] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [isAnimating, setIsAnimating] = useState(false);
  const [superAdminName, setSuperAdminName] = useState('');

  useEffect(() => {
    const fetchSuperAdminName = async () => {
      try {
        const pbAuth = JSON.parse(localStorage.getItem('pocketbase_auth') || '{}');
        let companyId = pbAuth?.record?.CompanyID;
        if (Array.isArray(companyId)) companyId = companyId[0];
        
        if (companyId) {
          const customer = await pb.collection('Customers').getOne(companyId);
          const superAdminContact = await pb.collection('Contacts').getOne(customer.SuperAdmin);
          setSuperAdminName(superAdminContact?.Name + " " +superAdminContact?.Surname || '');
        }
      } catch (err) {
        console.error('Error fetching SuperAdmin name:', err);
      }
    };

    fetchSuperAdminName();
  }, []);

  const getCardIcon = (brand) => {
    switch (brand?.toLowerCase()) {
      case 'visa':
        return <FaCcVisa className="text-2xl" />;
      case 'mastercard':
        return <FaCcMastercard className="text-2xl" />;
      case 'amex':
        return <FaCcAmex className="text-2xl" />;
      case 'discover':
        return <FaCcDiscover className="text-2xl" />;
      default:
        return <FaCreditCard className="text-2xl" />;
    }
  };

  const getCardTheme = (brand) => {
    switch (brand?.toLowerCase()) {
      case 'visa':
        return 'from-[#1A1F71] to-[#4B4B9C]';
      case 'mastercard':
        return 'from-[#EB001B] to-[#F79E1B]';
      case 'amex':
        return 'from-[#006FCF] to-[#2E77BB]';
      case 'discover':
        return 'from-[#FF6000] to-[#FFA000]';
      default:
        return 'from-[#0a1744] to-[#1a2a6c]';
    }
  };

  const handleStart = (e) => {
    if (isAnimating) return;
    setIsDragging(true);
    const pos = e.touches ? e.touches[0] : e;
    setStartPos({ x: pos.clientX, y: pos.clientY });
  };

  const handleMove = (e) => {
    if (!isDragging || isAnimating) return;
    e.preventDefault();
    const pos = e.touches ? e.touches[0] : e;
    const deltaX = pos.clientX - startPos.x;
    const deltaY = pos.clientY - startPos.y;

    setRotation(prev => ({
      x: prev.x + deltaY * 0.1,
      y: prev.y - deltaX * 0.1
    }));

    setStartPos({ x: pos.clientX, y: pos.clientY });
  };

  const handleEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    
    // Calculate the nearest 360-degree rotation
    const targetY = Math.round(rotation.y / 360) * 360;
    const currentY = rotation.y;
    
    // Animate to the nearest full rotation
    setIsAnimating(true);
    const duration = 500; // 500ms animation
    const startTime = performance.now();
    
    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function for smooth animation
      const easeOut = (t) => 1 - Math.pow(1 - t, 3);
      const easedProgress = easeOut(progress);
      
      setRotation(prev => ({
        x: prev.x * (1 - easedProgress),
        y: currentY + (targetY - currentY) * easedProgress
      }));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setRotation({ x: 0, y: targetY });
        setIsAnimating(false);
      }
    };
    
    requestAnimationFrame(animate);
  };

  useEffect(() => {
    if (cardRef.current) {
      cardRef.current.style.transform = `perspective(1000px) rotateX(${rotation.x}deg) rotateY(${rotation.y}deg)`;
    }
  }, [rotation]);

  return (
    <div className="relative w-full max-w-md mx-auto mb-6">
      <div 
        ref={cardRef}
        className="relative w-full max-w-md mx-auto transition-transform duration-100 cursor-grab active:cursor-grabbing"
        style={{ 
          transformStyle: 'preserve-3d',
          transition: isAnimating ? 'none' : 'transform 0.1s ease-out'
        }}
        onMouseDown={handleStart}
        onMouseMove={handleMove}
        onMouseUp={handleEnd}
        onMouseLeave={handleEnd}
        onTouchStart={handleStart}
        onTouchMove={handleMove}
        onTouchEnd={handleEnd}
      >
        <div className={`bg-gradient-to-r ${getCardTheme(card?.brand)} rounded-xl p-6 text-white shadow-lg transform-gpu`}>
          {/* Card Shine Effect */}
          <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300" />
          
          {/* Card Hologram */}
          <div className="absolute top-4 right-4 w-12 h-8 bg-gradient-to-r from-white/20 to-transparent rounded-lg" />
          
          {/* Card Chip */}
          <div className="w-12 h-8 bg-yellow-400/20 rounded-lg mb-6 flex items-center justify-center">
            <div className="w-8 h-6 bg-yellow-400/40 rounded" />
          </div>

          <div className="flex justify-between items-start mb-8">
            <div className="text-xl font-bold">Atraqll</div>
            {getCardIcon(card?.brand)}
          </div>

          <div className="mb-6">
            <div className="text-sm text-gray-300 mb-1">Card Number</div>
            <div className="text-xl tracking-wider font-mono">
              •••• •••• •••• {card?.last4 || '0000'}
            </div>
          </div>

          <div className="flex justify-between items-end">
            <div>
              <div className="text-sm text-gray-300 mb-1">Expires</div>
              <div className="text-lg font-mono">
                {card?.expMonth?.toString().padStart(2, '0') || '00'}/{card?.expYear?.toString().slice(-2) || '00'}
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-300 mb-1">Card Holder</div>
              <div className="text-lg">{card?.cardHolderName || superAdminName || 'CARD HOLDER'}</div>
            </div>
          </div>

          {/* Card Signature Strip */}
          <div className="absolute bottom-6 right-6 w-24 h-8 bg-white/10 rounded" />
        </div>

        {/* Card Reflection */}
        <div className="absolute -bottom-4 left-0 right-0 h-4 bg-gradient-to-t from-black/10 to-transparent blur-sm transform scale-x-95" />
      </div>
    </div>
  );
};

const Purchased = () => {
  const [seats, setSeats] = useState(0);
  const [pendingSeats, setPendingSeats] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('upfront');
  /** @type {[Card[], (cards: Card[]) => void]} */
  const [savedCards, setSavedCards] = useState([]);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [showCardForm, setShowCardForm] = useState(false);
  const [showBankDetails, setShowBankDetails] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [stripeCustomerId, setStripeCustomerId] = useState(null);
  const [stripeSubscriptionId, setStripeSubscriptionId] = useState(null);
  const [isGenesis, setIsGenesis] = useState(false);
  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponValidating, setCouponValidating] = useState(false);
  const [couponResult, setCouponResult] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const stripe = useStripe();
  const elements = useElements();
  const { currentCompanyId } = useCompany();

  const SEAT_PRICE = 70;
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const authToken = getAuthToken();

  const getCardTheme = (brand) => {
    switch (brand?.toLowerCase()) {
      case 'visa':
        return 'from-[#1A1F71] to-[#4B4B9C]';
      case 'mastercard':
        return 'from-[#EB001B] to-[#F79E1B]';
      case 'amex':
        return 'from-[#006FCF] to-[#2E77BB]';
      case 'discover':
        return 'from-[#FF6000] to-[#FFA000]';
      default:
        return 'from-[#0a1744] to-[#1a2a6c]';
    }
  };

  const getCardIcon = (brand) => {
    switch (brand?.toLowerCase()) {
      case 'visa':
        return <FaCcVisa className="w-8 h-8" />;
      case 'mastercard':
        return <FaCcMastercard className="w-8 h-8" />;
      case 'amex':
        return <FaCcAmex className="w-8 h-8" />;
      case 'discover':
        return <FaCcDiscover className="w-8 h-8" />;
      default:
        return <FaCreditCard className="w-8 h-8" />;
    }
  };

  useEffect(() => {
    fetchCustomerData();
  }, [currentCompanyId]);

  const fetchCustomerData = async () => {
    try {
      setLoading(true);
      const pbAuth = JSON.parse(localStorage.getItem('pocketbase_auth') || '{}');
      let companyId = currentCompanyId || pbAuth?.record?.CompanyID;
      if (Array.isArray(companyId)) companyId = companyId[0];

      if (!companyId) {
        throw new Error('Company ID not found');
      }

      // Fetch customer data from Customers collection
      const customer = await pb.collection('Customers').getOne(companyId);
      
      // Set billing enabled status
      setIsGenesis(customer.genesis === true);
      
      // Set seats from customer data
      setSeats(customer.Seats || 0);

      // Store subscription ID if present
      if (customer.stripeSubscriptionId) {
        setStripeSubscriptionId(customer.stripeSubscriptionId);
      }
      
      // Check for existing Stripe customer ID
      if (customer.stripeCustId) {
        setStripeCustomerId(customer.stripeCustId);
        // Fetch saved payment methods for this customer
        const response = await fetch(`${API_BASE_URL}/api/stripe/list-payment-methods`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken ? `Bearer ${authToken}` : '',
          },
          body: JSON.stringify({
            customerId: customer.stripeCustId,
          }),
        });

        if (response.ok) {
          const { paymentMethods } = await response.json();
          setSavedCards(paymentMethods);
          if (paymentMethods.length > 0) {
            setSelectedCardId(paymentMethods[0].id);
          }
        }
      }

      setError(null);
    } catch (err) {
      console.error('Error fetching customer data:', err);
      setError('Failed to load customer data');
    } finally {
      setLoading(false);
    }
  };

  const createStripeCustomer = async () => {
    try {
      const pbAuth = JSON.parse(localStorage.getItem('pocketbase_auth') || '{}');
      let companyId = currentCompanyId || pbAuth?.record?.CompanyID;
      if (Array.isArray(companyId)) companyId = companyId[0];
      console.log("companyId" , companyId)
      if (!companyId) {
        throw new Error('Company ID not found');
      }
      
      // Fetch customer details from Customers
      const customer = await pb.collection('Customers').getOne(companyId);
      
      // Fetch the SuperAdmin contact details to get the email
      const superAdminContact = await pb.collection('Contacts').getOne(customer.SuperAdmin);
      
      if (!superAdminContact?.email) {
        throw new Error('SuperAdmin email not found');
      }
      
      // Create customer in Stripe
      const response = await fetch(`${API_BASE_URL}/api/stripe/create-stripe-customer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authToken ? `Bearer ${authToken}` : '',
        },
        body: JSON.stringify({
          email: superAdminContact.email,
          name: customer.CompanyName,
          metadata: {
            companyId: companyId,
          },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create Stripe customer');
      }

      const { customerId } = await response.json();
      
      // Update customer in PocketBase with Stripe customer ID
      await pb.collection('Customers').update(companyId, {
        stripeCustId: customerId,
      });

      setStripeCustomerId(customerId);
      return customerId;
    } catch (err) {
      console.error('Error creating Stripe customer:', err);
      throw err;
    }
  };

  const handleCardSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) return;

    try {
      setProcessingPayment(true);

      // Get the current customer's Stripe ID from PocketBase
      const pbAuth = JSON.parse(localStorage.getItem('pocketbase_auth') || '{}');
      let companyId = currentCompanyId || pbAuth?.record?.CompanyID;
      if (Array.isArray(companyId)) companyId = companyId[0];
      
      if (!companyId) {
        throw new Error('Company ID not found');
      }

      // Fetch customer data to get the Stripe customer ID
      const customer = await pb.collection('Customers').getOne(companyId);
      let currentStripeCustomerId = customer.stripeCustId;

      // If no Stripe customer exists, create one
      if (!currentStripeCustomerId) {
        currentStripeCustomerId = await createStripeCustomer();
      }

      const setupResponse = await fetch(`${API_BASE_URL}/api/stripe/create-setup-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authToken ? `Bearer ${authToken}` : '',
        },
        body: JSON.stringify({
          customerId: currentStripeCustomerId,
          livemode: customer.genesis === true,
          email: customer.Email || customer.email || undefined,
          name: customer.CompanyName || customer.Name || undefined,
        }),
      });

      let paymentMethodId = null;
      if (setupResponse.ok) {
        const setupData = await setupResponse.json();
        if (setupData.customerId && setupData.customerId !== currentStripeCustomerId) {
          await pb.collection('Customers').update(companyId, { stripeCustId: setupData.customerId });
          currentStripeCustomerId = setupData.customerId;
          setStripeCustomerId(setupData.customerId);
        }
        // Link fills this Card Element with a saved card. Confirming the SetupIntent
        // attaches that card to the customer. A separate attach call is rejected for those cards.
        const confirmation = await stripe.confirmCardSetup(setupData.clientSecret, {
          payment_method: { card: cardElement },
        });
        if (confirmation.error) {
          setError(confirmation.error.message);
          return;
        }
        paymentMethodId = confirmation.setupIntent?.payment_method || null;
      } else if (setupResponse.status !== 404) {
        const setupError = await setupResponse.json().catch(() => ({}));
        throw new Error(setupError.error || 'Failed to start card setup');
      } else {
        const created = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
        });
        if (created.error) {
          setError(created.error.message);
          return;
        }
        paymentMethodId = created.paymentMethod?.id || null;
      }

      if (!paymentMethodId) {
        throw new Error('Stripe did not return a card to save');
      }

      const attachResponse = await fetch(`${API_BASE_URL}/api/stripe/attach-payment-method`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authToken ? `Bearer ${authToken}` : '',
        },
        body: JSON.stringify({
          customerId: currentStripeCustomerId,
          paymentMethodId,
        }),
      });
      const attachData = await attachResponse.json().catch(() => ({}));
      if (!attachResponse.ok) {
        throw new Error(attachData.error || 'Failed to attach payment method');
      }
      if (attachData.customerId && attachData.customerId !== currentStripeCustomerId) {
        await pb.collection('Customers').update(companyId, { stripeCustId: attachData.customerId });
        setStripeCustomerId(attachData.customerId);
      }

      // Refresh the list of saved cards
      await fetchCustomerData();
      
      setShowCardForm(false);
      setError(null);
    } catch (err) {
      console.error('Error saving card:', err);
      setError(err.message || 'Failed to save card');
    } finally {
      setProcessingPayment(false);
    }
  };

  const openPaymentModal = () => {
    if (!stripeCustomerId) {
      setError('Please add a payment method first');
      return;
    }
    if (paymentMethod === 'card' && !selectedCardId) {
      setError('Please select a payment method');
      return;
    }
    // Reset coupon state each time modal opens
    setCouponCode('');
    setCouponResult(null);
    setCouponError('');
    setAppliedCoupon(null);
    setShowPaymentModal(true);
  };

  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code.');
      return;
    }
    setCouponValidating(true);
    setCouponError('');
    setCouponResult(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/stripe/validate-coupon`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authToken ? `Bearer ${authToken}` : '',
        },
        body: JSON.stringify({
          code: couponCode.trim(),
          customerId: stripeCustomerId,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.valid) {
        setCouponError(data.message || 'Invalid or expired coupon code.');
        setCouponResult(null);
      } else {
        setCouponResult(data);
        setCouponError('');
      }
    } catch (err) {
      setCouponError('Failed to validate coupon. Please try again.');
    } finally {
      setCouponValidating(false);
    }
  };

  const handlePayment = async () => {
    setShowPaymentModal(false);
    try {
      setProcessingPayment(true);

      if (couponResult && couponResult.valid) {
        // Apply coupon flow
        const applyRes = await fetch(`${API_BASE_URL}/api/stripe/apply-coupon`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken ? `Bearer ${authToken}` : '',
          },
          body: JSON.stringify({
            code: couponCode.trim(),
            customerId: stripeCustomerId,
            additionalSeats: pendingSeats,
            subscriptionId: stripeSubscriptionId || undefined,
          }),
        });
        if (!applyRes.ok) throw new Error('Failed to apply coupon');
      } else {
        // Standard invoice flow
        const response = await fetch(`${API_BASE_URL}/api/stripe/create-invoice`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken ? `Bearer ${authToken}` : '',
          },
          body: JSON.stringify({
            customerId: stripeCustomerId,
            additionalSeats: pendingSeats,
          }),
        });
        if (!response.ok) throw new Error('Failed to create invoice');
      }

      // Refresh data from server instead of manual updates
      await fetchCustomerData();

      setPendingSeats(0);
      setAppliedCoupon(null);
      setCouponResult(null);
      setError(null);
    } catch (err) {
      console.error('Payment error:', err);
      setError('Failed to process payment');
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleSeatsChange = (newSeats) => {
    if (newSeats < 0) return;
    setPendingSeats(newSeats - seats);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }
  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-[#0a1744] mb-8">
          Manage Seats
        </h1>

        {/* Billing Verification / Genesis Check */}
        {!isGenesis ? (
          <div className="bg-white rounded-lg shadow-xl border-2 border-red-100 p-12 text-center max-w-2xl mx-auto my-12">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FaLock className="text-red-500 text-3xl" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Billing Not Enabled</h2>
            <p className="text-gray-600 text-lg mb-8">
              Your billing is not enabled, please contact Atraq Support for it.
            </p>
            <div className="flex flex-col gap-4 items-center">
              <button 
                onClick={() => window.location.href = '/ticket/create'}
                className="px-8 py-3 bg-[#0a1744] text-white rounded-lg hover:bg-opacity-90 transition-all font-medium"
              >
                Contact Support
              </button>
              <p className="text-sm text-gray-500 italic">
                Enablement is required to manage seats and payment methods.
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Quick Add Payment Method */}
            {stripeCustomerId && (
              <div className="mb-8 flex justify-end">
                <button
                  onClick={() => setShowCardForm(true)}
                  className="px-6 py-2 bg-[#0a1744] text-white rounded-lg hover:bg-[#A1E744] hover:text-[#0a1744] transition-all flex items-center gap-2 shadow-md font-medium"
                >
                  <FaCreditCard />
                  Add a payment method
                </button>
              </div>
            )}
            {/* Error Message */}
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                {error}
              </div>
            )}

            {/* Saved Cards Section */}
            {savedCards.length > 0 && (
              <div className="bg-white rounded-lg shadow-lg border-2 border-gray-100 p-6 mb-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold text-[#0a1744]">Your Saved Payment Methods</h2>
                  <button
                    onClick={() => setShowCardForm(true)}
                    className="px-4 py-2 text-[#0a1744] border-2 border-[#0a1744] rounded-lg hover:bg-[#0a1744] hover:text-white transition-colors flex items-center gap-2"
                  >
                    <FaCreditCard />
                    Add New Card
                  </button>
                </div>

                {/* Security Notice */}
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <FaLock className="h-5 w-5 text-blue-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-700">
                        Your card information is securely stored with Stripe. We never store your card details on our servers.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Australian Compliance Notice */}
                <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-green-700">
                        This payment system complies with Australian Payment Card Industry (PCI) standards and is regulated by the Australian Securities and Investments Commission (ASIC).
                      </p>
                    </div>
                  </div>
                </div>

                {/* Cards Display */}
                <div className="relative">
                  <div className="flex flex-wrap gap-6 justify-start">
                    {savedCards.map((card) => (
                      <div 
                        key={card.id} 
                        className="relative group w-full md:w-[calc(33.333%-1rem)] transition-all duration-300"
                        style={{ zIndex: selectedCardId === card.id ? 10 : 1 }}
                      >
                        <div 
                          className={`transform transition-all duration-300 ${
                            selectedCardId === card.id 
                              ? 'scale-105 shadow-xl' 
                              : 'hover:scale-102 hover:shadow-lg'
                          } cursor-pointer`}
                          onClick={() => setSelectedCardId(card.id)}
                        >
                          <RotatableCard card={card} />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-opacity duration-300 rounded-xl" />
                          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-b-xl">
                            <div className="text-white text-sm">
                              <p>Expires {card.expMonth}/{card.expYear}</p>
                              <p className="text-xs mt-1">Click to select for payment</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Seats Panel */}
            <div className="bg-white rounded-lg shadow-lg border-2 border-gray-100 p-6 mb-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-[#0a1744]">Seat Management</h2>
                <div className="text-sm text-gray-600">
                  <span className="font-medium">${SEAT_PRICE}</span> per seat
                </div>
              </div>
              
              {/* Seat Counter */}
              <div className="flex flex-col items-center justify-center gap-4 mb-6">
                <div className="text-center mb-4">
                  <span className="text-5xl font-bold text-[#0a1744]">
                    {seats + pendingSeats}
                  </span>
                  <div className="text-sm text-gray-600 mt-2">Total Seats</div>
                </div>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => handleSeatsChange(seats + pendingSeats - 1)}
                    className="w-14 h-14 flex items-center justify-center bg-[#0a1744] text-white rounded-full hover:bg-[#A1E744] hover:text-[#0a1744] transition-colors text-2xl font-bold disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
                    disabled={seats + pendingSeats <= 0}
                    title="Remove one seat"
                  >
                    -
                  </button>
                  <div className="w-24 h-24 bg-gradient-to-br from-[#0a1744] to-[#1a2a6c] rounded-full flex items-center justify-center shadow-lg relative group">
                    <div className="text-3xl font-bold text-white">
                      {seats + pendingSeats}
                    </div>
                  </div>
                  <button
                    onClick={() => handleSeatsChange(seats + pendingSeats + 1)}
                    className="w-14 h-14 flex items-center justify-center bg-[#0a1744] text-white rounded-full hover:bg-[#A1E744] hover:text-[#0a1744] transition-colors text-2xl font-bold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
                    title="Add one seat"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Warning Message */}
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      Note: Each Communicator (user) is counted as one seat. Make sure to account for all team members who will need access to the system.
                    </p>
                  </div>
                </div>
              </div>

              {pendingSeats !== 0 && (
                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-blue-700 font-medium">
                      {pendingSeats > 0 ? '+' : ''}{pendingSeats} seats pending payment
                    </p>
                    <p className="text-blue-700 font-medium">
                      Total: ${Math.abs(pendingSeats * SEAT_PRICE)}
                    </p>
                  </div>
                  <div className="mt-4 space-y-4">
                    <div className="flex items-center space-x-4">
                      <input
                        type="radio"
                        id="upfront"
                        name="paymentMethod"
                        value="upfront"
                        checked={paymentMethod === 'upfront'}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="h-4 w-4 text-[#0a1744]"
                      />
                      <label htmlFor="upfront" className="text-gray-700">Upfront Payment</label>
                    </div>
                    <div className="flex items-center space-x-4">
                      <input
                        type="radio"
                        id="card"
                        name="paymentMethod"
                        value="card"
                        checked={paymentMethod === 'card'}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="h-4 w-4 text-[#0a1744]"
                      />
                      <label htmlFor="card" className="text-gray-700">Credit Card</label>
                    </div>
                  </div>

                  {paymentMethod === 'upfront' && (
                    <div className="mt-4">
                      <button
                        onClick={() => setShowBankDetails(!showBankDetails)}
                        className="text-[#0a1744] hover:text-[#A1E744] underline flex items-center gap-2"
                      >
                        <FaBuilding />
                        {showBankDetails ? 'Hide' : 'Show'} Bank Details
                      </button>
                      {showBankDetails && (
                        <div className="mt-2 p-4 bg-gray-50 rounded-lg">
                          <h3 className="font-semibold mb-2">US$ Account Details</h3>
                          <p className="text-sm">Name: Comon Professionals Pty. Ltd.</p>
                          <p className="text-sm">Bank: Commonwealth Bank of Australia</p>
                          <p className="text-sm">Branch: 064793</p>
                          <p className="text-sm">Account No: 10310327</p>
                          <p className="text-sm">IBAN: 06479310310327</p>
                        </div>
                      )}
                    </div>
                  )}

                  {paymentMethod === 'card' && (
                    <div className="mt-4">
                      {savedCards.length > 0 ? (
                        <div className="space-y-3">
                          <h3 className="font-semibold text-gray-700">Select Payment Method</h3>
                          {savedCards.map((card) => (
                            <div
                              key={card.id}
                              className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all ${
                                selectedCardId === card.id
                                  ? 'border-[#0a1744] bg-[#0a1744] bg-opacity-5'
                                  : 'border-gray-200 hover:border-[#0a1744]'
                              }`}
                              onClick={() => setSelectedCardId(card.id)}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-4">
                                  <div className={`w-12 h-8 rounded ${getCardTheme(card.brand)} flex items-center justify-center text-white`}>
                                    {getCardIcon(card.brand)}
                                  </div>
                                  <div>
                                    <p className="font-medium">•••• •••• •••• {card.last4}</p>
                                    <p className="text-sm text-gray-600">Expires {card.expMonth}/{card.expYear}</p>
                                  </div>
                                </div>
                                <div className="flex items-center">
                                  <input
                                    type="radio"
                                    checked={selectedCardId === card.id}
                                    readOnly
                                    className="h-4 w-4 text-[#0a1744]"
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <button
                          onClick={() => setShowCardForm(true)}
                          className="mt-2 px-4 py-2 bg-[#0a1744] text-white rounded-lg hover:bg-opacity-90 transition-colors"
                        >
                          Add Payment Method
                        </button>
                      )}
                    </div>
                  )}

                  <button
                    onClick={openPaymentModal}
                    className="mt-4 px-6 py-2 bg-[#0a1744] text-white rounded-lg hover:bg-[#A1E744] hover:text-[#0a1744] transition-colors w-full flex items-center justify-center gap-2"
                    disabled={processingPayment}
                  >
                    {processingPayment ? (
                      <>
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                      </>
                    ) : (
                      <>
                        <FaCreditCard />
                        Complete Payment
                      </>
                    )}
                  </button>
                </div>
              )}

              <div className="mt-6 text-sm text-gray-600">
                <p>All features are included with your subscription. Add more seats to accommodate additional team members.</p>
              </div>
            </div>
          </>
        )}

        {/* Payment Confirmation Modal */}
        {showPaymentModal && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative animate-fade-in">
              {/* Close */}
              <button
                onClick={() => setShowPaymentModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <h2 className="text-2xl font-bold text-[#0a1744] mb-1">Confirm Payment</h2>
              <p className="text-sm text-gray-500 mb-6">Review your order and apply a coupon if you have one.</p>

              {/* Order Summary */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6 border border-gray-100">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Order Summary</h3>
                <div className="flex justify-between text-sm text-gray-700 mb-2">
                  <span>Seats</span>
                  <span className="font-medium">{pendingSeats}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-700 mb-2">
                  <span>Price per seat</span>
                  <span className="font-medium">${SEAT_PRICE}</span>
                </div>
                <div className="border-t border-gray-200 my-3" />
                {couponResult && couponResult.valid ? (
                  <>
                    <div className="flex justify-between text-sm text-gray-700 mb-1">
                      <span>Subtotal</span>
                      <span className="font-medium">${couponResult.calculation.originalAmount}</span>
                    </div>
                    <div className="flex justify-between text-sm text-green-600 mb-1">
                      <span>Discount ({couponResult.coupon.discountType === 'percentage' ? `${couponResult.coupon.discountValue}%` : `$${couponResult.coupon.discountValue}`})</span>
                      <span className="font-medium">-${couponResult.calculation.discountAmount}</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold text-[#0a1744] mt-2">
                      <span>Total</span>
                      <span>${couponResult.calculation.finalAmount}</span>
                    </div>
                  </>
                ) : (
                  <div className="flex justify-between text-lg font-bold text-[#0a1744]">
                    <span>Total</span>
                    <span>${pendingSeats * SEAT_PRICE}</span>
                  </div>
                )}
              </div>

              {/* Coupon Section */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Have a coupon code?</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => {
                      setCouponCode(e.target.value.toUpperCase());
                      setCouponResult(null);
                      setCouponError('');
                    }}
                    placeholder="Enter coupon code"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0a1744] focus:border-transparent transition-all"
                    disabled={couponValidating}
                  />
                  <button
                    onClick={handleValidateCoupon}
                    disabled={couponValidating || !couponCode.trim()}
                    className="px-4 py-2 bg-[#0a1744] text-white text-sm rounded-lg hover:bg-[#A1E744] hover:text-[#0a1744] transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium whitespace-nowrap"
                  >
                    {couponValidating ? (
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : 'Validate'}
                  </button>
                </div>

                {/* Coupon error */}
                {couponError && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    {couponError}
                  </div>
                )}

                {/* Coupon success */}
                {couponResult && couponResult.valid && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                    <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>
                      Coupon <strong>{couponResult.coupon.code}</strong> applied — you save{' '}
                      {couponResult.coupon.discountType === 'percentage'
                        ? `${couponResult.coupon.discountValue}%`
                        : `$${couponResult.coupon.discountValue}`}!
                    </span>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handlePayment}
                  className="flex-1 px-5 py-3 bg-[#0a1744] text-white rounded-xl font-semibold hover:bg-[#A1E744] hover:text-[#0a1744] transition-all flex items-center justify-center gap-2 shadow-md"
                >
                  <FaCreditCard />
                  {couponResult && couponResult.valid
                    ? `Pay $${couponResult.calculation.finalAmount}`
                    : `Pay $${pendingSeats * SEAT_PRICE}`}
                </button>
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="px-5 py-3 text-gray-600 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Card Form Modal */}
        {showCardForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative">
              <button
                onClick={() => setShowCardForm(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <h2 className="text-xl font-semibold text-[#0a1744] mb-4">Add Payment Method</h2>
              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  {error}
                </div>
              )}
              
              {/* Australian Compliance Notice */}
              <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-green-700">
                      This payment system complies with Australian Payment Card Industry (PCI) standards.
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleCardSubmit}>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Card Details</label>
                  <div className="p-4 border border-gray-300 rounded-lg">
                    <CardElement
                      options={{
                        style: {
                          base: {
                            fontSize: '16px',
                            color: '#424770',
                            '::placeholder': {
                              color: '#aab7c4',
                            },
                          },
                          invalid: {
                            color: '#9e2146',
                          },
                        },
                        hidePostalCode: true,
                      }}
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2 text-sm text-gray-600 mb-6">
                  <FaLock />
                  <span>Your payment information is secure and encrypted</span>
                </div>

                <div className="flex gap-3">
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-[#0a1744] text-white rounded-lg hover:bg-[#A1E744] hover:text-[#0a1744] transition-colors"
                  >
                    Save Card
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCardForm(false)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Wrap the component with Stripe Elements provider
const PurchasedWithStripe = () => {
  const { currentCompanyId } = useCompany();
  const [stripePromise, setStripePromise] = useState(/** @type {any} */ (null));
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    const initializeStripe = async () => {
      try {
        const pbAuth = JSON.parse(localStorage.getItem('pocketbase_auth') || '{}');
        let companyId = currentCompanyId || pbAuth?.record?.CompanyID;
        if (Array.isArray(companyId)) companyId = companyId[0];

        if (companyId) {
          const customer = await pb.collection('Customers').getOne(companyId);
          // Check for 'genesis' boolean field
          const key = customer?.genesis === true ? STRIPE_LIVE_KEY : STRIPE_TEST_KEY;
          setStripePromise(loadStripe(key));
        } else {
          setStripePromise(loadStripe(STRIPE_TEST_KEY));
        }
      } catch (err) {
        console.error('Error initializing Stripe with companyId:', currentCompanyId, err);
        setStripePromise(loadStripe(STRIPE_TEST_KEY));
      } finally {
        setInitializing(false);
      }
    };

    initializeStripe();
  }, [currentCompanyId]);

  if (initializing) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0a1744]"></div>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <Purchased />
    </Elements>
  );
};

export default PurchasedWithStripe;